import React, { useState } from 'react';
import './ExerciseRoutineBuilder.css';
import { EXERCISE_LIBRARY, PRESET_WORKOUTS, getExerciseByName } from '../data/exercises';

function ExerciseRoutineBuilder({ onCreateRoutine, onClose }) {
  const [routineName, setRoutineName] = useState('');
  const [selectedExercises, setSelectedExercises] = useState([]);
  const [recurring, setRecurring] = useState('daily');
  const [selectedPreset, setSelectedPreset] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [equipmentFilter, setEquipmentFilter] = useState('all');
  const [primaryMuscleFilter, setPrimaryMuscleFilter] = useState('all');
  const [secondaryMuscleFilter, setSecondaryMuscleFilter] = useState('all');

  const loadPreset = (presetKey) => {
    if (!presetKey) {
      setSelectedExercises([]);
      setRoutineName('');
      return;
    }
    
    const preset = PRESET_WORKOUTS[presetKey];
    setRoutineName(preset.name);
    
    const exercises = preset.exercises
      .map(name => getExerciseByName(name))
      .filter(Boolean)
      .map(exercise => ({ ...exercise }));
    
    setSelectedExercises(exercises);
    setSelectedPreset(presetKey);
  };

  // Get unique categories for filter dropdown
  const categories = ['all', ...new Set(EXERCISE_LIBRARY.map(e => e.category))].sort();
  const equipmentOptions = ['all', ...new Set(EXERCISE_LIBRARY.flatMap(e => e.equipment || []))].sort();
  const primaryMuscleOptions = ['all', ...new Set(EXERCISE_LIBRARY.flatMap(e => e.primaryMuscles || []))].sort();
  const secondaryMuscleOptions = ['all', ...new Set(EXERCISE_LIBRARY.flatMap(e => e.secondaryMuscles || []))].sort();

  const formatLabel = (value) => value
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  // Filter exercises based on search and category
  const filteredExercises = EXERCISE_LIBRARY.filter(exercise => {
    const matchesSearch = exercise.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || exercise.category === categoryFilter;
    const matchesEquipment =
      equipmentFilter === 'all' || (exercise.equipment || []).includes(equipmentFilter);
    const matchesPrimary =
      primaryMuscleFilter === 'all' || (exercise.primaryMuscles || []).includes(primaryMuscleFilter);
    const matchesSecondary =
      secondaryMuscleFilter === 'all' || (exercise.secondaryMuscles || []).includes(secondaryMuscleFilter);
    return matchesSearch && matchesCategory && matchesEquipment && matchesPrimary && matchesSecondary;
  });

  const toggleExercise = (exercise) => {
    const exists = selectedExercises.find(e => e.name === exercise.name);
    if (exists) {
      setSelectedExercises(selectedExercises.filter(e => e.name !== exercise.name));
    } else {
      setSelectedExercises([...selectedExercises, { ...exercise }]);
    }
  };

  const updateExercise = (index, field, value) => {
    const updated = [...selectedExercises];
    updated[index][field] = parseInt(value);
    setSelectedExercises(updated);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Save as a routine for the SessionTracker
    const newRoutine = {
      id: `routine_${Date.now()}`,
      name: routineName || 'Custom Routine',
      exercises: selectedExercises.map(exercise => ({
        name: exercise.name,
        sets: exercise.sets,
        reps: exercise.reps,
        difficulty: exercise.difficulty,
        category: exercise.category
      })),
      recurring: recurring,
      createdAt: new Date().toISOString()
    };

    // Save routine to localStorage
    const existingRoutines = JSON.parse(localStorage.getItem('workout_routines') || '[]');
    existingRoutines.push(newRoutine);
    localStorage.setItem('workout_routines', JSON.stringify(existingRoutines));

    // Also create individual tasks for legacy support
    selectedExercises.forEach((exercise, index) => {
      const taskData = {
        title: `${routineName || 'Workout'} - ${exercise.name}`,
        description: `Part ${index + 1} of ${selectedExercises.length}`,
        type: 'exercise',
        difficulty: exercise.difficulty,
        sets: exercise.sets,
        reps: exercise.reps,
        duration: exercise.duration || 0,
        recurring: recurring
      };
      onCreateRoutine(taskData);
    });

    if (onClose) onClose();
  };

  const totalExercises = selectedExercises.length;
  const totalDifficulty = selectedExercises.reduce((sum, e) => sum + e.difficulty, 0);

  return (
    <div className="routine-builder-overlay" onClick={onClose}>
      <div className="routine-builder" onClick={(e) => e.stopPropagation()}>
        <div className="routine-builder-header">
          <h2> Build Exercise Routine</h2>
          <button className="close-btn" onClick={onClose}></button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Quick Start - Preset Workouts */}
          <div className="form-group">
            <label>Quick Start - Preset Workouts</label>
            <div className="preset-buttons">
              <button
                type="button"
                className={`preset-btn ${selectedPreset === 'day1' ? 'active' : ''}`}
                onClick={() => loadPreset('day1')}
              >
                 Day 1: Glute & Hamstring
              </button>
              <button
                type="button"
                className={`preset-btn ${selectedPreset === 'day2' ? 'active' : ''}`}
                onClick={() => loadPreset('day2')}
              >
                 Day 2: Glute & Calf
              </button>
              <button
                type="button"
                className="preset-btn"
                onClick={() => loadPreset('')}
              >
                 Custom
              </button>
            </div>
          </div>

          {/* Routine Name */}
          <div className="form-group">
            <label htmlFor="routineName">Routine Name</label>
            <input
              id="routineName"
              type="text"
              value={routineName}
              onChange={(e) => setRoutineName(e.target.value)}
              placeholder="Morning Workout"
              required
            />
          </div>

          {/* Exercise Library */}
          <div className="form-group">
            <label>Select Exercises ({totalExercises} selected)</label>
            
            {/* Search and Filter Bar */}
            <div className="exercise-filter-bar">
              <input
                type="text"
                placeholder=" Search exercises..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="exercise-search"
              />
              <select 
                value={categoryFilter} 
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="category-filter"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>
                    {cat === 'all' ? ' All Categories' : formatLabel(cat)}
                  </option>
                ))}
              </select>
              <select
                value={equipmentFilter}
                onChange={(e) => setEquipmentFilter(e.target.value)}
                className="category-filter"
              >
                {equipmentOptions.map(option => (
                  <option key={option} value={option}>
                    {option === 'all' ? ' All Equipment' : formatLabel(option)}
                  </option>
                ))}
              </select>
              <select
                value={primaryMuscleFilter}
                onChange={(e) => setPrimaryMuscleFilter(e.target.value)}
                className="category-filter"
              >
                {primaryMuscleOptions.map(option => (
                  <option key={option} value={option}>
                    {option === 'all' ? ' Primary Muscle' : formatLabel(option)}
                  </option>
                ))}
              </select>
              <select
                value={secondaryMuscleFilter}
                onChange={(e) => setSecondaryMuscleFilter(e.target.value)}
                className="category-filter"
              >
                {secondaryMuscleOptions.map(option => (
                  <option key={option} value={option}>
                    {option === 'all' ? ' Secondary Muscle' : formatLabel(option)}
                  </option>
                ))}
              </select>
            </div>

            {/* Exercise Library */}
            <div className="exercise-library">
              {filteredExercises.map((exercise, idx) => {
                const isSelected = selectedExercises.find(e => e.name === exercise.name);
                return (
                  <button
                    key={exercise.name}
                    type="button"
                    className={`exercise-card ${isSelected ? 'selected' : ''}`}
                    onClick={() => toggleExercise(exercise)}
                  >
                    <div className="exercise-name">{exercise.name}</div>
                    <div className="exercise-meta">
                      {exercise.duration 
                        ? `${exercise.duration}s` 
                        : `${exercise.sets}x${exercise.reps}`}
                    </div>
                  </button>
                );
              })}
            </div>
            {filteredExercises.length === 0 && (
              <div className="no-results">No exercises found. Try adjusting your search or filter.</div>
            )}
          </div>

          {/* Selected Exercises Customization */}
          {selectedExercises.length > 0 && (
            <div className="selected-exercises">
              <h3> Customize Your Routine</h3>
              {selectedExercises.map((exercise, idx) => (
                <div key={idx} className="exercise-customizer">
                  <div className="exercise-title">{exercise.name}</div>
                  <div className="exercise-inputs">
                    <div className="input-group">
                      <label>Sets</label>
                      <input
                        type="number"
                        value={exercise.sets}
                        onChange={(e) => updateExercise(idx, 'sets', e.target.value)}
                        min="1"
                        max="10"
                      />
                    </div>
                    <div className="input-group">
                      <label>Reps</label>
                      <input
                        type="number"
                        value={exercise.reps}
                        onChange={(e) => updateExercise(idx, 'reps', e.target.value)}
                        min="1"
                        max="100"
                      />
                    </div>
                    {exercise.duration !== undefined && (
                      <div className="input-group">
                        <label>Duration (s)</label>
                        <input
                          type="number"
                          value={exercise.duration}
                          onChange={(e) => updateExercise(idx, 'duration', e.target.value)}
                          min="10"
                          max="300"
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Recurring */}
          <div className="form-group">
            <label>Repeat</label>
            <div className="recurring-buttons">
              <button
                type="button"
                className={`recurring-btn ${recurring === 'daily' ? 'active' : ''}`}
                onClick={() => setRecurring('daily')}
              >
                 Daily
              </button>
              <button
                type="button"
                className={`recurring-btn ${recurring === 'weekly' ? 'active' : ''}`}
                onClick={() => setRecurring('weekly')}
              >
                 Weekly
              </button>
              <button
                type="button"
                className={`recurring-btn ${recurring === null ? 'active' : ''}`}
                onClick={() => setRecurring(null)}
              >
                Once
              </button>
            </div>
          </div>

          {/* Summary */}
          {totalExercises > 0 && (
            <div className="routine-summary">
              <div className="summary-stat">
                <span className="stat-label">Exercises:</span>
                <span className="stat-value">{totalExercises}</span>
              </div>
              <div className="summary-stat">
                <span className="stat-label">Difficulty:</span>
                <span className="stat-value">
                  {''.repeat(Math.min(5, Math.ceil(totalDifficulty / totalExercises)))}
                </span>
              </div>
            </div>
          )}

          {/* Submit */}
          <div className="form-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button 
              type="submit" 
              className="submit-btn"
              disabled={selectedExercises.length === 0}
            >
               Create Routine
            </button>
          </div>
        </form>

        <div className="snoopy-corner"></div>
      </div>
    </div>
  );
}

export default ExerciseRoutineBuilder;
